import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, Wifi, Activity, AlertCircle, ArrowLeft, TrendingUp } from 'lucide-react'

export default function NetworkMonitorPage() {
  const navigate = useNavigate()
  const [networks, setNetworks] = useState([
    { id: 1, name: 'Primary Network', status: 'Online', latency: '2ms', bandwidth: '950 Mbps', devices: 156, uptime: '99.99%' },
    { id: 2, name: 'Backup Network', status: 'Online', latency: '5ms', bandwidth: '480 Mbps', devices: 42, uptime: '99.95%' },
    { id: 3, name: 'Guest Network', status: 'Online', latency: '8ms', bandwidth: '95 Mbps', devices: 28, uptime: '99.80%' },
    { id: 4, name: 'VPN Gateway', status: 'Online', latency: '15ms', bandwidth: '250 Mbps', devices: 45, uptime: '99.98%' },
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingNetwork, setEditingNetwork] = useState(null)
  const [formData, setFormData] = useState({ name: '', status: 'Online', latency: '', bandwidth: '', devices: 0, uptime: '' })

  const stats = useMemo(() => ({
    totalNetworks: networks.length,
    online: networks.filter(n => n.status === 'Online').length,
    avgLatency: (networks.reduce((sum, n) => sum + parseInt(n.latency), 0) / networks.length).toFixed(1),
    totalDevices: networks.reduce((sum, n) => sum + n.devices, 0)
  }), [networks])

  const handleCreate = () => {
    setEditingNetwork(null)
    setFormData({ name: '', status: 'Online', latency: '', bandwidth: '', devices: 0, uptime: '' })
    setShowModal(true)
  }

  const handleEdit = (network) => {
    setEditingNetwork(network)
    setFormData(network)
    setShowModal(true)
  }

  const handleSave = () => {
    if (!formData.name) return alert('Network name required')
    if (editingNetwork) {
      setNetworks(networks.map(n => n.id === editingNetwork.id ? { ...n, ...formData } : n))
    } else {
      setNetworks([...networks, { id: Date.now(), ...formData }])
    }
    setShowModal(false)
  }

  const handleDelete = (id) => {
    if (confirm('Delete this network?')) {
      setNetworks(networks.filter(n => n.id !== id))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/network')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Network Management
        </button>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Network Monitor</h1>
              <p className="text-slate-400">Real-time network status and performance tracking</p>
            </div>
            <button onClick={handleCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
              <Plus size={20} /> Add Network
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Networks</div>
            <div className="text-3xl font-bold text-blue-400">{stats.totalNetworks}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Online</div>
            <div className="text-3xl font-bold text-green-400">{stats.online}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg Latency</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.avgLatency}ms</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Devices</div>
            <div className="text-3xl font-bold text-purple-400">{stats.totalDevices}</div>
          </div>
        </div>

        <div className="space-y-4">
          {networks.map((network) => (
            <div key={network.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Wifi className="text-blue-400" size={20} />
                    <h3 className="text-lg font-semibold text-white">{network.name}</h3>
                    <span className="px-3 py-1 rounded text-xs font-semibold bg-green-600/20 text-green-400">{network.status}</span>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Latency</p>
                      <p className="text-white font-bold mt-1">{network.latency}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Bandwidth</p>
                      <p className="text-white font-bold mt-1">{network.bandwidth}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Connected Devices</p>
                      <p className="text-white font-bold mt-1">{network.devices}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Uptime</p>
                      <p className="text-white font-bold mt-1">{network.uptime}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(network)} className="p-2 hover:bg-blue-500/20 rounded">
                    <Edit2 size={18} className="text-blue-400" />
                  </button>
                  <button onClick={() => handleDelete(network.id)} className="p-2 hover:bg-red-500/20 rounded">
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">{editingNetwork ? 'Edit Network' : 'Add Network'}</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Network Name" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" value={formData.latency} onChange={(e) => setFormData({...formData, latency: e.target.value})} placeholder="Latency (ms)" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                  <input type="text" value={formData.bandwidth} onChange={(e) => setFormData({...formData, bandwidth: e.target.value})} placeholder="Bandwidth" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input type="number" value={formData.devices} onChange={(e) => setFormData({...formData, devices: parseInt(e.target.value)})} placeholder="Connected Devices" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                  <input type="text" value={formData.uptime} onChange={(e) => setFormData({...formData, uptime: e.target.value})} placeholder="Uptime %" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                </div>
                <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                  <option>Online</option>
                  <option>Offline</option>
                  <option>Maintenance</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSave} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
